import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, MessageSquare, Search, Trash2, Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { LlmChatSessionDto, LlmChatSessionStatus } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { ConfirmButton } from '@/components/ConfirmButton';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useInquiries } from '@/hooks/useInquiry';

interface LlmChatSessionSidebarProps {
  sessions: LlmChatSessionDto[];
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
  onDeleteSession: (id: string) => Promise<void>;
  deletingSessionId?: string | null;
  isLoading?: boolean;
  isCreatingSession?: boolean;
  newSessionLabel?: string;
  /** Route inquiry context (enables filter toggle when set) */
  scopeInquiryId?: string | null;
  scopeInquiryTitle?: string | null;
  filterByCurrentInquiry?: boolean;
  onFilterByCurrentInquiryChange?: (value: boolean) => void;
}

const ORPHAN_INQUIRY_KEY = '__none__';

function sessionMatchesSearch(session: LlmChatSessionDto, search: string): boolean {
  if (!search) return true;
  return (session.title?.toLowerCase().includes(search) ?? false);
}

function SessionRow({
  session,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  deletingSessionId,
}: {
  session: LlmChatSessionDto;
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string) => Promise<void>;
  deletingSessionId: string | null;
}) {
  const { t, i18n } = useTranslation();

  return (
    <div
      className={cn(
        'group flex items-start gap-0.5 rounded-md transition-colors hover:bg-accent',
        activeSessionId === session.id && 'bg-accent',
      )}
    >
      <button
        type="button"
        onClick={() => onSelectSession(session.id)}
        className="flex min-w-0 flex-1 items-start gap-3 px-3 py-2.5 text-left text-sm"
      >
        <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">
            {session.title || t('llmChat.untitledSession')}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span>
              {new Date(session.updatedAt).toLocaleString(i18n.language, {
                dateStyle: 'short',
                timeStyle: 'short',
              })}
            </span>
            {session.status === LlmChatSessionStatus.Completed && (
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                {t('llmChat.completed')}
              </Badge>
            )}
          </div>
        </div>
      </button>
      <ConfirmButton
        title={t('llmChat.deleteSession')}
        description={t('llmChat.deleteSessionConfirm')}
        confirmText="generic.confirmDelete"
        cancelText="generic.cancel"
        variant="ghost"
        loading={deletingSessionId === session.id}
        disabled={deletingSessionId !== null && deletingSessionId !== session.id}
        className="mt-1.5 h-7 w-7 shrink-0 p-0 text-muted-foreground opacity-70 hover:text-destructive group-hover:opacity-100"
        onConfirm={() => onDeleteSession(session.id)}
      >
        <span className="sr-only">{t('llmChat.deleteSession')}</span>
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </ConfirmButton>
    </div>
  );
}

export function LlmChatSessionSidebar({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewSession,
  onDeleteSession,
  deletingSessionId = null,
  isLoading,
  isCreatingSession = false,
  newSessionLabel,
  scopeInquiryId = null,
  scopeInquiryTitle,
  filterByCurrentInquiry = true,
  onFilterByCurrentInquiryChange,
}: LlmChatSessionSidebarProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data: inquiries = [] } = useInquiries();

  const inquiryTitleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const inquiry of inquiries) {
      if (inquiry.title) map.set(inquiry.id, inquiry.title);
    }
    return map;
  }, [inquiries]);

  const searchLower = search.trim().toLowerCase();

  const filtered = useMemo(
    () => sessions.filter((s) => sessionMatchesSearch(s, searchLower)),
    [sessions, searchLower],
  );

  const showScopedList = !!scopeInquiryId && filterByCurrentInquiry;

  const grouped = useMemo(() => {
    if (showScopedList) return null;

    const buckets = new Map<string, LlmChatSessionDto[]>();
    for (const session of filtered) {
      const key = session.inquiryId ?? ORPHAN_INQUIRY_KEY;
      const list = buckets.get(key) ?? [];
      list.push(session);
      buckets.set(key, list);
    }

    const groups = Array.from(buckets.entries()).map(([key, list]) => {
      const sorted = [...list].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      const latestUpdated = sorted[0]?.updatedAt ?? '';
      const label =
        key === ORPHAN_INQUIRY_KEY
          ? t('llmChat.sessionsWithoutInquiry')
          : (inquiryTitleById.get(key) ?? t('llmChat.untitledInquiry'));
      return { key, label, sessions: sorted, latestUpdated };
    });

    groups.sort(
      (a, b) => new Date(b.latestUpdated).getTime() - new Date(a.latestUpdated).getTime(),
    );
    return groups;
  }, [filtered, inquiryTitleById, showScopedList, t]);

  const emptyMessage = showScopedList
    ? t('llmChat.noSessionsForInquiry')
    : t('llmChat.noSessions');

  return (
    <div className="flex h-full w-72 flex-col border-r bg-muted/30">
      <div className="flex items-center gap-2 border-b p-3">
        <Button
          onClick={onNewSession}
          size="sm"
          className="w-full gap-2"
          disabled={isCreatingSession}
        >
          {isCreatingSession ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {newSessionLabel ?? t('llmChat.newSession')}
        </Button>
      </div>

      {scopeInquiryId && (
        <div className="space-y-2 border-b px-3 py-2">
          {filterByCurrentInquiry && scopeInquiryTitle && (
            <p className="text-xs text-muted-foreground">
              {t('llmChat.sessionsForInquiry', { title: scopeInquiryTitle })}
            </p>
          )}
          {onFilterByCurrentInquiryChange && (
            <div className="flex items-center justify-between gap-2">
              <Label
                htmlFor="llm-chat-inquiry-filter"
                className="cursor-pointer text-xs font-normal leading-snug"
              >
                {t('llmChat.filterByCurrentInquiry')}
              </Label>
              <Switch
                id="llm-chat-inquiry-filter"
                checked={filterByCurrentInquiry}
                onCheckedChange={onFilterByCurrentInquiryChange}
              />
            </div>
          )}
        </div>
      )}

      <div className="p-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('llmChat.searchSessions')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        ) : showScopedList || !grouped ? (
          <div className="space-y-1 p-2">
            {filtered.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                activeSessionId={activeSessionId}
                onSelectSession={onSelectSession}
                onDeleteSession={onDeleteSession}
                deletingSessionId={deletingSessionId}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3 p-2">
            {grouped.map((group) => (
              <div key={group.key}>
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {group.label}
                </div>
                <div className="space-y-1">
                  {group.sessions.map((session) => (
                    <SessionRow
                      key={session.id}
                      session={session}
                      activeSessionId={activeSessionId}
                      onSelectSession={onSelectSession}
                      onDeleteSession={onDeleteSession}
                      deletingSessionId={deletingSessionId}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
